import * as React from "react";

interface ChartMargin {
  top: number;
  left: number;
}

interface GridLinesProps {
  margin: ChartMargin;
  innerWidth: number;
  innerHeight: number;
}

export const ChartGridLines = ({ margin, innerWidth, innerHeight }: GridLinesProps) => (
  <>
    {Array.from({ length: 5 }, (_, i) => {
      const y = margin.top + (innerHeight / 4) * i;
      return (
        <g key={`grid-${i}`}>
          <line
            x1={margin.left}
            y1={y}
            x2={margin.left + innerWidth}
            y2={y}
            stroke="#e9e0d5"
            strokeWidth={1}
            strokeDasharray="2,2"
          />
        </g>
      );
    })}
  </>
);

interface Props {
  x: number;
  baseY: number;
  barWidth: number;
  totalBarHeight: number;
  total: number;
  done: number;
  inProgress: number;
  overdue: number;
  isHovered: boolean;
  getStatusColor: (status: string) => string;
  onMouseEnter: (event: React.MouseEvent<SVGRectElement, MouseEvent>) => void;
  onMouseLeave: () => void;
  /** Receives the y coordinate of the top of the stack. */
  renderLabel: (topY: number) => React.ReactNode;
}

/** One stacked bar: done at the bottom, then in-progress, then overdue on top. */
export const StackedStatusBar = ({
  x,
  baseY,
  barWidth,
  totalBarHeight,
  total,
  done,
  inProgress,
  overdue,
  isHovered,
  getStatusColor,
  onMouseEnter,
  onMouseLeave,
  renderLabel,
}: Props) => {
  const doneHeight = total > 0 ? (done / total) * totalBarHeight : 0;
  const inProgressHeight = total > 0 ? (inProgress / total) * totalBarHeight : 0;
  const overdueHeight = total > 0 ? (overdue / total) * totalBarHeight : 0;

  const doneY = baseY - doneHeight;
  const inProgressY = doneY - inProgressHeight;
  const overdueY = inProgressY - overdueHeight;

  const segments = [
    { key: "done", count: done, y: doneY, height: doneHeight },
    { key: "in_progress", count: inProgress, y: inProgressY, height: inProgressHeight },
    { key: "overdue", count: overdue, y: overdueY, height: overdueHeight },
  ];

  return (
    <g>
      {segments.map(
        segment =>
          segment.count > 0 && (
            <rect
              key={segment.key}
              x={x}
              y={segment.y}
              width={barWidth}
              height={segment.height}
              fill={getStatusColor(segment.key)}
              stroke="white"
              strokeWidth={1}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                opacity: isHovered ? 0.8 : 1,
              }}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            />
          ),
      )}
      {renderLabel(overdueY)}
    </g>
  );
};
